import {
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkHnwStatement,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import UKAppropriatenessWarningDialog from "./UKAppropriatenessWarningDialog";

interface UKHNWInvestorStatementProps {
  // When provided, the component runs in public (token-only) mode: it skips the
  // authenticated API call and Redux step dispatch and defers navigation to the
  // parent instead.
  onPublicContinue?: () => void;
  onPublicBack?: () => void;
}

export default function UKHNWInvestorStatement({
  onPublicContinue,
  onPublicBack,
}: UKHNWInvestorStatementProps = {}) {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();

  const [declarationAgreed, setDeclarationAgreed] = useState(
    ukOnboarding.hnwStatement.declarationAgreed,
  );
  const [error, setError] = useState<string | null>(null);
  const [warningOpen, setWarningOpen] = useState(false);

  const [submitHnwStatement, { isLoading }] = api.useSubmitHnwInvestorStatementMutation();

  const handleContinue = () => {
    if (!declarationAgreed) {
      setError("You must agree to the declaration to continue.");
      return;
    }
    setError(null);
    setWarningOpen(true);
  };

  const handleProceed = async () => {
    if (onPublicContinue) {
      setWarningOpen(false);
      onPublicContinue();
      return;
    }

    try {
      await submitHnwStatement({
        submitHnwInvestorStatementDto: { declarationAgreed: true },
      }).unwrap();

      dispatch(
        setUkHnwStatement({
          declarationAgreed: true,
        }),
      );
      dispatch(setApplicationStep("ukAssessment"));
    } catch (err: any) {
      setWarningOpen(false);
      setError(err?.data?.message || "Failed to submit your statement. Please try again.");
    }
  };

  const handleBack = () => {
    if (onPublicBack) {
      onPublicBack();
      return;
    }
    dispatch(setApplicationStep("ukInvestorType"));
  };

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          High Net Worth Investor Statement
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          As a High Net Worth Investor, you have fewer restrictions on your investment amounts.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            High Net Worth Investor Declaration
          </Typography>
          <Typography variant="body1" paragraph>
            I declare that I qualify as a High Net Worth Investor because I meet at least one
            of the following criteria:
          </Typography>
          <Stack spacing={2} ml={2} mb={2}>
            <Typography variant="body1">
              <strong>1. Annual Income:</strong> I had an annual income of £100,000 or more
              during the last financial year.
            </Typography>
            <Typography variant="body1">
              <strong>2. Net Assets:</strong> I have net assets of £250,000 or more (excluding
              primary residence, pensions, and life insurance policies).
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Net assets</strong> means total assets minus total liabilities, excluding:
          </Typography>
          <Stack spacing={1} ml={2} mb={2}>
            <Typography variant="body2" color="text.secondary">
              • The value of your primary residence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Any pension funds
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Any life insurance policies
            </Typography>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: "#FFF8E1",
            border: "1px solid #FFE082",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" paragraph>
            <strong>Important:</strong> By making this declaration, you confirm that:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              1. You meet one or both of the criteria above to qualify as a High Net Worth
              Investor.
            </Typography>
            <Typography variant="body2">
              2. You understand that this is a self-certification and that Caleb & Brown
              relies on the accuracy of this declaration.
            </Typography>
            <Typography variant="body2">
              3. Being a high net worth investor will expose me to promotions for investment
              where there is a risk of losing all the money I invest. I am aware that it is
              open to me to seek professional advice before making any investment in a
              high-risk investment.
            </Typography>
            <Typography variant="body2">
              4. You understand that providing false information may have legal consequences
              and may result in account closure.
            </Typography>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={declarationAgreed}
              onChange={(e) => setDeclarationAgreed(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I confirm that I have read and understood the above declaration, and that I
              qualify as a High Net Worth Investor as defined by the FCA.
            </Typography>
          }
        />
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          mt={2}
        >
          <Button variant="outlined" color="inherit" size="large" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            disabled={!declarationAgreed || isLoading}
            sx={{ minWidth: 200 }}
          >
            {isLoading ? "Submitting..." : "Continue"}
          </Button>
        </Stack>
      </Grid>

      <UKAppropriatenessWarningDialog
        open={warningOpen}
        isLoading={isLoading}
        investorType="hnw"
        onClose={() => setWarningOpen(false)}
        onProceed={handleProceed}
        onExit={() => {
          setWarningOpen(false);
          handleBack();
        }}
      />
    </Grid>
  );
}
